import * as React from 'react'

import { XIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { ISignature, SignatureType } from '~/client/graph/types.generated'
import BaseCompactPopup from '~/client/src/shared/components/BaseCompactPopup/BaseCompactPopup'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { NOOP } from '~/client/src/shared/utils/noop'

import SignatureCanvas from './SignatureCanvas'
import SignatureTyped from './SignatureTyped'

import './SignatureModal.scss'

type SignatureMode = 'type' | 'draw'

interface IProps {
  isShown: boolean
  userId: string
  initialSignature?: ISignature
  onConfirm: (signature: ISignature) => void
  onCancel: () => void
}

@observer
export default class SignatureModal extends React.Component<IProps> {
  private canvasRef = React.createRef<SignatureCanvas>()
  private typedRef = React.createRef<SignatureTyped>()

  @observable private mode: SignatureMode = 'type'
  @observable private currentDataUrl: string | null = null
  @observable private currentFontFamily: string = ''
  @observable private typedSignerName: string = ''

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    if (this.props.isShown && !prevProps.isShown) {
      this.initializeFromSignature()
    }
  }

  @action.bound
  private initializeFromSignature() {
    const { initialSignature } = this.props

    if (initialSignature?.dataUrl) {
      this.currentDataUrl = initialSignature.dataUrl
      this.currentFontFamily = initialSignature.fontFamily || ''
      this.typedSignerName = initialSignature.typedValue || ''
      this.mode =
        initialSignature.signatureType === SignatureType.Drawn ? 'draw' : 'type'

      setTimeout(() => {
        if (this.mode === 'draw' && this.canvasRef.current) {
          this.canvasRef.current.loadFromDataUrl(initialSignature.dataUrl)
        } else if (this.mode === 'type' && this.typedRef.current) {
          this.typedRef.current.initialize(
            initialSignature.typedValue || '',
            initialSignature.fontFamily,
          )
        }
      }, 0)
    } else {
      this.currentDataUrl = null
      this.currentFontFamily = ''
      this.typedSignerName = ''
      this.mode = 'type'
    }
  }

  public render() {
    const { isShown, onCancel } = this.props
    const onOutsideClickHandler = isShown ? onCancel : NOOP

    return (
      <MenuCloser
        className={classList({
          'signature-modal-wrapper': true,
          'is-shown': isShown,
        })}
        closeMenu={onOutsideClickHandler}
        isOpen={isShown}
      >
        <BaseCompactPopup
          title={Localization.translator.signature}
          isShown={isShown}
          onHide={onCancel}
          actionButtonTitle={Localization.translator.apply}
          isActionButtonEnabled={!!this.currentDataUrl}
          onActionButtonClick={this.handleConfirm}
          containerClassName="signature-modal-container"
          childrenClassName="signature-modal-content-wrapper"
        >
          <div className="signature-modal-tabs row">
            <button
              type="button"
              className={classList({
                'signature-modal-tab': true,
                active: this.mode === 'type',
              })}
              onClick={this.setTypeMode}
            >
              {Localization.translator.type}
            </button>
            <button
              type="button"
              className={classList({
                'signature-modal-tab': true,
                active: this.mode === 'draw',
              })}
              onClick={this.setDrawMode}
            >
              {Localization.translator.draw}
            </button>
          </div>

          <div className="signature-modal-content col">
            {this.mode === 'draw' ? (
              <SignatureCanvas
                ref={this.canvasRef}
                onSignatureChange={this.handleDrawSignatureChange}
              />
            ) : (
              <SignatureTyped
                ref={this.typedRef}
                onSignatureChange={this.handleTypedSignatureChange}
              />
            )}
          </div>

          <div className="signature-modal-controls row x-end y-center">
            <button
              type="button"
              className="signature-clear-btn row y-center"
              onClick={this.handleClear}
            >
              <span>{Localization.translator.clear}</span>
              <XIcon size={16} className="mr4" />
            </button>
          </div>
        </BaseCompactPopup>
      </MenuCloser>
    )
  }

  @action.bound
  private setTypeMode() {
    this.mode = 'type'
    this.currentDataUrl = null
  }

  @action.bound
  private setDrawMode() {
    this.mode = 'draw'
    this.currentDataUrl = null
  }

  @action.bound
  private handleDrawSignatureChange(dataUrl: string | null) {
    this.currentDataUrl = dataUrl
    this.currentFontFamily = ''
  }

  @action.bound
  private handleTypedSignatureChange(
    dataUrl: string | null,
    fontFamily: string,
    typedText: string,
  ) {
    this.currentDataUrl = dataUrl
    this.currentFontFamily = fontFamily
    this.typedSignerName = typedText
  }

  @action.bound
  private handleClear() {
    if (this.mode === 'draw' && this.canvasRef.current) {
      this.canvasRef.current.clear()
    } else if (this.mode === 'type' && this.typedRef.current) {
      this.typedRef.current.clear()
    }
    this.currentDataUrl = null
  }

  @action.bound
  private handleConfirm() {
    if (!this.currentDataUrl) return

    const { userId, onConfirm } = this.props

    const signature: ISignature = {
      dataUrl: this.currentDataUrl,
      timestamp: Date.now(),
      typedValue: this.mode === 'type' ? this.typedSignerName : undefined,
      signatureType:
        this.mode === 'type' ? SignatureType.Typed : SignatureType.Drawn,
      fontFamily: this.mode === 'type' ? this.currentFontFamily : undefined,
      userId,
    }

    onConfirm(signature)
  }
}
